import { Check, Loader2, Plus, QrCode, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BaseSheet } from '@/components/common/BaseSheet';
import { PosterPageFrame, POSTER_PAGE_W, POSTER_PAGE_H } from '@/components/activation/poster/PosterPageFrame';
import { POSTER_DESIGNS } from '@/components/activation/poster/posterDesigns';
import { LookbookPageFrame, LOOKBOOK_PAGE_W, LOOKBOOK_PAGE_H } from '@/components/activation/lookbook/LookbookPageFrame';
import { LOOKBOOK_DESIGNS } from '@/components/activation/lookbook/lookbookDesigns';
import { resolveLookbookBrand } from '@/components/activation/lookbook/lookbookBrand';
import { ActivationPackWithTarget, ASSET_TYPE_INFO } from '@/types/activation/activation-pack';
import { PosterDesign } from '@/types/activation/poster';
import { LookbookDesign } from '@/types/activation/lookbook';
import { PrintOrderItem } from '@/types/activation/print-order';

const DESIGN_ADD_QTY = 10;

interface AddPrintItemsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pack: ActivationPackWithTarget;
  items: PrintOrderItem[];
  brand?: string;
  onAddPosterDesign: (design: PosterDesign) => void;
  isAddingPoster: boolean;
  onAddLookbookDesign: (design: LookbookDesign) => void;
  isAddingLookbook: boolean;
  onAddAsset: (type: 'sticker_pdf' | 'app_poster_pdf') => void;
}

/** A tiny live-rendered, portrait-scaled A4 page — same trick as the design galleries. */
const ScaledPage = ({ width, naturalW, naturalH, children }: { width: number; naturalW: number; naturalH: number; children: React.ReactNode }) => {
  const scale = width / naturalW;
  return (
    <div
      style={{ width, height: naturalH * scale }}
      className="relative overflow-hidden rounded-lg bg-white shadow-[0_6px_16px_rgba(15,23,42,0.14)]"
    >
      <div style={{ position: 'absolute', top: 0, left: 0, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
        {children}
      </div>
    </div>
  );
};

const DesignTile = ({
  width,
  naturalW,
  naturalH,
  added,
  disabled,
  brand,
  label,
  onAdd,
  children,
}: {
  width: number;
  naturalW: number;
  naturalH: number;
  added: boolean;
  disabled: boolean;
  brand: string;
  label: string;
  onAdd: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onAdd}
    disabled={added || disabled}
    aria-label={added ? `${label} — already in your order` : `Add ${label}`}
    className="group relative shrink-0 rounded-lg transition-transform disabled:cursor-default enabled:hover:scale-[1.03]"
  >
    <ScaledPage width={width} naturalW={naturalW} naturalH={naturalH}>
      {children}
    </ScaledPage>
    <span className="mt-1.5 block truncate text-center text-[11px] font-medium text-gray-600" style={{ width }}>
      {label}
    </span>
    {added ? (
      <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full text-white shadow" style={{ backgroundColor: brand }}>
        <Check className="h-3 w-3" />
      </span>
    ) : (
      <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-gray-900/80 text-white opacity-0 shadow transition-opacity group-hover:opacity-100">
        {disabled ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
      </span>
    )}
  </button>
);

export const AddPrintItemsDialog = ({
  open,
  onOpenChange,
  pack,
  items,
  brand = '#4E9CFF',
  onAddPosterDesign,
  isAddingPoster,
  onAddLookbookDesign,
  isAddingLookbook,
  onAddAsset,
}: AddPrintItemsDialogProps) => {
  const posterBrand = resolveLookbookBrand(pack);

  // While any design is rasterizing/uploading, every add surface pauses —
  // a second add mid-flight would race the first one's write to the order.
  const busy = isAddingPoster || isAddingLookbook;

  const hasSticker = items.some((i) => i.assetType === 'sticker_pdf');
  const hasAppPoster = items.some((i) => i.assetType === 'app_poster_pdf');
  const stickerReady = pack.touchpoints.qr_sticker && !!pack.generated_assets?.sticker_pdf?.url;
  const appPosterReady = !!pack.generated_assets?.app_poster_pdf?.url;

  const addedPosterIds = new Set(
    items.filter((i) => i.id.startsWith('design:poster:')).map((i) => i.id.slice('design:poster:'.length)),
  );
  const addedLookbookIds = new Set(
    items.filter((i) => i.id.startsWith('design:lookbook:')).map((i) => i.id.slice('design:lookbook:'.length)),
  );

  return (
    <BaseSheet
      isOpen={open}
      onClose={() => onOpenChange(false)}
      title="Add to your print order"
      description="Choose the production files and quantities to send to your printer."
      size="lg"
      preventOutsideClick={busy}
      footer={(
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
          Done
        </Button>
      )}
    >
      <div className="space-y-6">
          {/* Quick add — the two ready-made defaults */}
          {(stickerReady || appPosterReady) && (
            <div className="flex flex-wrap gap-3">
              {stickerReady && (
                <button
                  type="button"
                  onClick={() => onAddAsset('sticker_pdf')}
                  disabled={hasSticker || busy}
                  className="flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors disabled:cursor-default"
                  style={hasSticker ? { borderColor: `${brand}55`, background: `${brand}08` } : { borderColor: `${brand}30` }}
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ backgroundColor: `${brand}12` }}>
                    <QrCode className="h-4 w-4" style={{ color: brand }} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900">{ASSET_TYPE_INFO.sticker_pdf.label}</div>
                    <div className="text-xs text-gray-500">{hasSticker ? 'Already in your order' : 'Add · 100 units'}</div>
                  </div>
                  {hasSticker ? <Check className="h-4 w-4 shrink-0" style={{ color: brand }} /> : <Plus className="h-4 w-4 shrink-0 text-gray-400" />}
                </button>
              )}
              {appPosterReady && (
                <button
                  type="button"
                  onClick={() => onAddAsset('app_poster_pdf')}
                  disabled={hasAppPoster || busy}
                  className="flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors disabled:cursor-default"
                  style={hasAppPoster ? { borderColor: `${brand}55`, background: `${brand}08` } : { borderColor: `${brand}30` }}
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ backgroundColor: `${brand}12` }}>
                    <Smartphone className="h-4 w-4" style={{ color: brand }} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900">{ASSET_TYPE_INFO.app_poster_pdf.label}</div>
                    <div className="text-xs text-gray-500">{hasAppPoster ? 'Already in your order' : 'Add · 10 units'}</div>
                  </div>
                  {hasAppPoster ? <Check className="h-4 w-4 shrink-0" style={{ color: brand }} /> : <Plus className="h-4 w-4 shrink-0 text-gray-400" />}
                </button>
              )}
            </div>
          )}

          {/* Poster designs */}
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Poster designs · one click adds {DESIGN_ADD_QTY} units
            </div>
            <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1">
              {POSTER_DESIGNS.map((design) => (
                <DesignTile
                  key={design.id}
                  width={64}
                  naturalW={POSTER_PAGE_W}
                  naturalH={POSTER_PAGE_H}
                  added={addedPosterIds.has(design.id)}
                  disabled={busy}
                  brand={brand}
                  label={design.name}
                  onAdd={() => onAddPosterDesign(design)}
                >
                  <PosterPageFrame brand={posterBrand} design={design} />
                </DesignTile>
              ))}
            </div>
          </div>

          {/* Lookbook designs */}
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Lookbook designs · one click adds {DESIGN_ADD_QTY} units
            </div>
            <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1">
              {LOOKBOOK_DESIGNS.map((design, index) => (
                <DesignTile
                  key={design.id}
                  width={64}
                  naturalW={LOOKBOOK_PAGE_W}
                  naturalH={LOOKBOOK_PAGE_H}
                  added={addedLookbookIds.has(design.id)}
                  disabled={busy}
                  brand={brand}
                  label={design.name}
                  onAdd={() => onAddLookbookDesign(design)}
                >
                  <LookbookPageFrame brand={posterBrand} design={design} index={index + 1} total={LOOKBOOK_DESIGNS.length} />
                </DesignTile>
              ))}
            </div>
          </div>

      </div>
    </BaseSheet>
  );
};
